import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Download,
  FileDown,
  Loader2,
  MoreHorizontal,
  Package,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { useNotification } from '@/components/notifications';
import {
  deleteSkill,
  downloadSkill,
  SkillNotPortableError,
} from '@/services/skills';
import type { Skill } from '@/services/skills';
import { useSkillList } from '@/hooks/queries/skills/use-skill-list';
import { useOrgStore } from '@/stores/org-store';
import { UploadSkillModal } from './upload-skill-modal';
import { EditSkillModal } from './edit-skill-modal';
import { AnatomyTree } from './anatomy-tree';
import { Identicon, hueForId } from './identicon';
import { Skeleton } from '@/components/ui/skeleton';

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

export const SkillsPage = () => {
  const [showUpload, setShowUpload] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState<Skill | null>(null);
  const [skillToEdit, setSkillToEdit] = useState<Skill | null>(null);
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;

  const { data, isLoading } = useSkillList();

  const deleteMutation = useMutation({
    mutationFn: deleteSkill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills', orgId] });
      notification.success('Skill deleted');
    },
    onError: () => {
      notification.error('Failed to delete skill');
    },
  });

  const skills = data?.skills ?? [];
  const downloadSupported = data?.download_supported === true;
  const rawDownloadSupported = data?.raw_download_supported === true;

  return (
    <div className='w-full'>
      <div className='flex justify-between items-start mb-6'>
        <div>
          <h1 className='text-2xl font-bold tracking-tight'>Skills</h1>
          <p className='mt-2 text-sm text-muted-foreground max-w-[75ch]'>
            Upload and manage skills for Code Interpreter agents. A skill is a
            .zip archive containing a SKILL.md file and any supporting files.
          </p>
        </div>
        <Button onClick={() => setShowUpload(true)} className='shrink-0 ml-4'>
          <Plus className='size-4 mr-2' />
          Upload Skill
        </Button>
      </div>

      {isLoading ? (
        <div className='space-y-3'>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className='relative flex items-center gap-3.5 rounded-xl border bg-card p-4 pl-6 shadow-xs'
            >
              <Skeleton className='absolute left-0 top-3.5 bottom-3.5 w-[3px] rounded-r-sm' />
              <div className='flex-1 min-w-0 flex flex-col gap-2'>
                <div className='flex items-center gap-2'>
                  <Skeleton className='size-[18px] rounded-sm' />
                  <Skeleton className='h-4 w-40' />
                </div>
                <Skeleton className='h-3 w-72' />
                <div className='flex items-center gap-2 mt-0.5'>
                  <Skeleton className='h-3 w-24' />
                  <Skeleton className='h-3 w-20' />
                </div>
              </div>
              <div className='flex items-center gap-0.5'>
                <Skeleton className='size-8 rounded-md' />
                <Skeleton className='size-8 rounded-md' />
              </div>
            </div>
          ))}
        </div>
      ) : skills.length === 0 ? (
        <div className='rounded-xl border shadow-sm'>
          <div className='flex items-stretch gap-12 p-14'>
            <div className='flex flex-1 flex-col justify-center max-w-[360px]'>
              <div className='mb-4 flex size-12 items-center justify-center rounded-lg bg-primary/10'>
                <Package className='size-[22px] text-primary' />
              </div>
              <h2 className='text-xl font-semibold tracking-tight mb-1.5'>
                No skills yet
              </h2>
              <p className='text-sm text-muted-foreground mb-5 leading-normal'>
                A skill is a .zip bundle: one{' '}
                <code className='font-mono text-xs'>SKILL.md</code> at the root,
                plus any scripts or examples the agent needs. Upload one to give
                Code Interpreter agents domain-specific expertise.
              </p>
              <Button onClick={() => setShowUpload(true)}>
                <Upload className='size-4 mr-2' />
                Upload Skill
              </Button>
            </div>
            <div className='flex flex-1 items-center justify-center min-w-0'>
              <AnatomyTree />
            </div>
          </div>
        </div>
      ) : (
        <div className='space-y-3'>
          {skills.map((skill: Skill) => (
            <SkillRow
              key={skill.skill_id}
              skill={skill}
              downloadSupported={downloadSupported}
              rawDownloadSupported={rawDownloadSupported}
              onEdit={() => setSkillToEdit(skill)}
              onDelete={() => setSkillToDelete(skill)}
            />
          ))}
        </div>
      )}

      {showUpload && (
        <UploadSkillModal
          open={showUpload}
          onClose={() => setShowUpload(false)}
          onSuccess={() => {
            setShowUpload(false);
            queryClient.invalidateQueries({ queryKey: ['skills', orgId] });
            notification.success('Skill uploaded');
          }}
        />
      )}

      {skillToEdit && (
        <EditSkillModal
          skill={skillToEdit}
          open={!!skillToEdit}
          onClose={() => setSkillToEdit(null)}
          onSuccess={() => {
            setSkillToEdit(null);
            queryClient.invalidateQueries({ queryKey: ['skills', orgId] });
            notification.success('Skill updated');
          }}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!skillToDelete}
        title='Delete Skill'
        description={`Are you sure you want to delete "${skillToDelete?.name}"? This action cannot be undone.`}
        buttonLabel='Delete'
        isLoading={deleteMutation.isPending}
        handleDelete={() => {
          if (skillToDelete) {
            deleteMutation.mutate(skillToDelete.skill_id, {
              onSuccess: () => setSkillToDelete(null),
            });
          }
        }}
        handleCancel={() => setSkillToDelete(null)}
      />
    </div>
  );
};

function SkillRow({
  skill,
  downloadSupported,
  rawDownloadSupported,
  onEdit,
  onDelete,
}: {
  skill: Skill;
  downloadSupported: boolean;
  rawDownloadSupported: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const notification = useNotification();
  const download = useMutation({
    mutationFn: (raw: boolean) => downloadSkill(skill, { raw }),
    onError: (err) => {
      const message = errorMessage(err, 'Could not download this skill');
      notification.error(
        err instanceof SkillNotPortableError && rawDownloadSupported
          ? `${message} To get its files as stored, use “Download files (raw)” in the ⋯ menu.`
          : message,
      );
    },
  });
  const hue = hueForId(skill.skill_id);
  const stripColor = `oklch(0.62 0.16 ${hue})`;

  return (
    <div className='relative flex items-center gap-3.5 rounded-xl border bg-card p-4 pl-6 shadow-xs'>
      <div
        className='absolute left-0 top-3.5 bottom-3.5 w-[3px] rounded-r-sm'
        style={{ background: stripColor }}
      />
      <div className='flex-1 min-w-0 flex flex-col gap-1'>
        <div className='flex items-center gap-2 min-w-0'>
          <Identicon name={skill.skill_id} />
          <h3 className='font-semibold text-sm tracking-tight truncate'>
            {skill.name}
          </h3>
        </div>
        {skill.description && (
          <p className='text-xs text-muted-foreground leading-snug line-clamp-2'>
            {skill.description}
          </p>
        )}
        <div className='flex items-center gap-2 text-xs text-muted-foreground mt-0.5'>
          <Package className='size-[13px] shrink-0' />
          <span>Skill archive</span>
          <span className='opacity-50'>·</span>
          <span>{new Date(skill.updated_at).toLocaleDateString()}</span>
          {skill.created_by && (
            <>
              <span className='opacity-50'>·</span>
              <span>{skill.created_by}</span>
            </>
          )}
        </div>
      </div>
      <div className='flex items-center gap-0.5 shrink-0'>
        {downloadSupported ? (
          <Button
            variant='ghost'
            size='icon'
            className='size-8 text-muted-foreground'
            aria-label='Download'
            disabled={download.isPending}
            onClick={() => download.mutate(false)}
          >
            {download.isPending ? (
              <Loader2 className='size-[15px] animate-spin' />
            ) : (
              <Download className='size-[15px]' />
            )}
          </Button>
        ) : (
          skill.skill_file_presigned_url && (
            <Button
              variant='ghost'
              size='icon'
              className='size-8 text-muted-foreground'
              asChild
            >
              <a
                href={skill.skill_file_presigned_url}
                download
                aria-label='Download'
              >
                <Download className='size-[15px]' />
              </a>
            </Button>
          )
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='size-8 text-muted-foreground'
              aria-label='More actions'
            >
              <MoreHorizontal className='size-[15px]' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className='size-4 mr-2' />
              Edit details
            </DropdownMenuItem>
            {rawDownloadSupported && (
              <DropdownMenuItem
                disabled={download.isPending}
                onClick={() => download.mutate(true)}
                title='The files exactly as stored, for skills Claude won’t accept as they are'
              >
                <FileDown className='size-4 mr-2' />
                Download files (raw)
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete}>
              <Trash2 className='size-4 mr-2' />
              Delete skill
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
